import { CloseOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { useGetPromotionsQuery } from "../../../../app/features/courses/courses.api";
import type { GetPromotionResponse } from "../../../../app/features/courses/courses.types";
import useError from "../../../../hooks/useError";
import { addZero, enToBnNumber } from "../../../../utils/number.utils";

type Promotion = GetPromotionResponse["items"][number];

type TimeLeft = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
};

const getTimeLeft = (endDate: string): TimeLeft => {
  const end = new Date(endDate).getTime();
  const now = Date.now();
  const diff = end - now;

  if (Number.isNaN(end) || diff <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      isExpired: true,
    };
  }

  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;

  return {
    days,
    hours,
    minutes,
    seconds,
    isExpired: false,
  };
};

function useCountdown(promotion?: Promotion) {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() =>
    promotion
      ? getTimeLeft(promotion.endsAt)
      : {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isExpired: true,
        },
  );

  useEffect(() => {
    if (!promotion) return;

    setTimeLeft(getTimeLeft(promotion.endsAt));

    const interval = setInterval(() => {
      setTimeLeft(getTimeLeft(promotion.endsAt));
    }, 1000);

    return () => clearInterval(interval);
  }, [promotion]);

  return timeLeft;
}

export default function TopPromotionBar() {
  const [closed, setClosed] = useState(false);
  const location = useLocation();

  const { data, isLoading, error } = useGetPromotionsQuery({
    activeOnly: true,
    hasCode: true,
    limit: 10,
  });
  useError(error, "ডিসকাউন্ট অফার লোড করা যায়নি");

  const promotion = useMemo(() => {
    if (!data?.items?.length) return undefined;

    const now = Date.now();

    return data.items.find((item) => {
      const endsAt = new Date(item.endsAt).getTime();
      return (
        item.status === "ACTIVE" &&
        !item.isHidden &&
        !Number.isNaN(endsAt) &&
        endsAt > now
      );
    });
  }, [data]);

  const timeLeft = useCountdown(promotion);

  if (closed || isLoading || !promotion || timeLeft.isExpired) {
    return null;
  }

  const { days, hours, minutes, seconds } = timeLeft;

  const toBn = (value: number) => enToBnNumber(addZero(value));

  const discountText =
    promotion.discountKind === "PERCENT"
      ? `${enToBnNumber(promotion.value)}% পর্যন্ত ছাড়`
      : promotion.discountKind === "FIXED_AMOUNT"
        ? `৳${enToBnNumber(promotion.value)} পর্যন্ত ছাড়`
        : "ফ্রি শিপিং অফার";

  if (location.pathname === "/user-manual") {
    return null;
  }

  return (
    <section className="flex items-center justify-center flex-wrap gap-4 bg-[#BCD8D8] px-4 py-3 md:px-6 relative">
      <p className="text-xs md:text-sm text-theme-zinc-700 font-normal">
        আমাদের কোর্সে পাচ্ছেন সর্বোচ্চ{" "}
        <span className="font-semibold">{discountText}</span> —{" "}
        <span>এখনই এনরোল করুন</span>
        <span className="hidden md:inline-block mx-2">|</span>
        <span className="block md:inline-block mt-1 md:mt-0">
          অফার শেষ হতে আর বাকি:
        </span>
      </p>

      <div className="flex items-center gap-3">
        <div className="inline-flex gap-1 px-2 py-1.5 bg-Base rounded-lg bg-theme-primary shadow-[inset_0px_-4px_4px_0px_rgba(0,0,0,0.20)] text-white">
          <TimeItem label="দিন" value={toBn(days)} />
          <Separator />
          <TimeItem label="ঘণ্টা" value={toBn(hours)} />
          <Separator />
          <TimeItem label="মিনিট" value={toBn(minutes)} />
          <Separator />
          <TimeItem label="সেকেন্ড" value={toBn(seconds)} />
        </div>

        <button
          type="button"
          onClick={() => setClosed(true)}
          className="text-theme-slate-500 hover:text-theme-zinc-700 transition-colors absolute right-4 top-1/2 -translate-y-1/2"
          aria-label="Close promotion bar"
        >
          <CloseOutlined />
        </button>
      </div>
    </section>
  );
}

interface TimeItemProps {
  label: string;
  value: string | number;
}

function TimeItem({ label, value }: TimeItemProps) {
  return (
    <div className="flex flex-col items-center justify-center min-w-[40px]">
      <span className="text-sm md:text-lg font-semibold leading-none [text-shadow:_0px_3px_4px_rgb(0_0_0_/_0.38)]">
        {value}
      </span>
      <span className="text-[9px]  leading-none">{label}</span>
    </div>
  );
}

function Separator() {
  return (
    <span className="text-sm md:text-base font-semibold leading-none px-0.5 [text-shadow:_0px_3px_4px_rgb(0_0_0_/_0.38)] mt-0.5">
      :
    </span>
  );
}
